import { Base, ReverCard, ReverLoading } from '@itsrever/design-system'
import { useTranslation } from 'react-i18next'
import {
  AddressField as Field,
  OnboardingAction,
  WarehouseAddress,
  addressProblems
} from '@/domain/onboarding'
import { Dispatch, useState } from 'react'
import { useCountries } from '@/application/queries'
import { Country } from '@/domain/countries'

const { FieldRoot, FieldLabel, FieldError, Input, SelectCombobox } = Base

interface WarehouseAddressFormProps {
  warehouseAddress: WarehouseAddress
  dispatch: Dispatch<OnboardingAction>
}

export const WarehouseAddressForm: React.FC<WarehouseAddressFormProps> = ({
  warehouseAddress,
  dispatch
}) => {
  const { t } = useTranslation()
  const { countries, loadingCountries } = useCountries()

  const [touched, setTouched] = useState<Partial<Record<Field, boolean>>>({})

  const touch = (field: Field) =>
    setTouched((prev) => ({ ...prev, [field]: true }))

  const problems = addressProblems(warehouseAddress)
  const shown = (field: Field) => (touched[field] ? problems[field] : undefined)

  const errorEmail = !!shown('email')
  const errorName = !!shown('name')
  const errorCompany = !!shown('company')
  const errorPhone = !!shown('phone')
  const errorCountry = !!shown('country')
  const errorCity = !!shown('city')
  const errorPostcode = !!shown('postcode')
  const addressLine1Problem = shown('addressLine1')
  const errorAddressLine2 = !!shown('addressLine2')

  const update = (field: Field, value: string) =>
    dispatch({
      type: 'UPDATE_WAREHOUSE_ADDRESS',
      payload: { ...warehouseAddress, [field]: value }
    })

  const selectedCountry =
    countries.find((country) => country.iso === warehouseAddress.countryCode) ??
    null

  return (
    <ReverCard className="p-4" fullWidth>
      {loadingCountries ? (
        <ReverLoading />
      ) : (
        <>
          <div className="mb-4">
            <h6>{t('onboarding.warehouse_address')}</h6>
            <p className="text-xs text-grey-3">
              {t('onboarding.contact_info_description')}
            </p>
          </div>
          <div className="flex flex-col gap-4">
            <AddressField
              label={t('onboarding.email')}
              placeholder="example@itsrever.com"
              type="email"
              value={warehouseAddress.email}
              onChange={(value) => update('email', value)}
              onBlur={() => touch('email')}
              error={errorEmail ? t('onboarding.error_email') : ''}
            />
            <div className="grid grid-cols-2 gap-4">
              <AddressField
                label={t('onboarding.name')}
                placeholder="John Doe"
                value={warehouseAddress.name}
                onChange={(value) => update('name', value)}
                onBlur={() => touch('name')}
                error={errorName ? t('onboarding.error_name') : ''}
              />
              <AddressField
                label={t('onboarding.company')}
                placeholder="Nike Shoes Inc."
                value={warehouseAddress.company}
                onChange={(value) => update('company', value)}
                onBlur={() => touch('company')}
                error={errorCompany ? t('onboarding.error_company') : ''}
              />
            </div>
            <AddressField
              label={t('onboarding.phone')}
              placeholder="+34 623456789"
              type="tel"
              value={warehouseAddress.phone}
              onChange={(value) => update('phone', value)}
              onBlur={() => touch('phone')}
              error={errorPhone ? t('onboarding.error_phone') : ''}
            />
            <FieldRoot invalid={errorCountry}>
              <FieldLabel>{t('onboarding.country')}</FieldLabel>
              <SelectCombobox<Country>
                items={countries}
                value={selectedCountry}
                onChange={(country) => {
                  touch('country')
                  dispatch({
                    type: 'UPDATE_WAREHOUSE_ADDRESS',
                    payload: {
                      ...warehouseAddress,
                      country: country?.name ?? '',
                      countryCode: country?.iso ?? ''
                    }
                  })
                }}
                itemLabel={(country) => country.name}
                itemKey={(country) => country.iso}
                placeholder={t('onboarding.country')}
                triggerLabel={t('onboarding.country')}
                onBlur={() => touch('country')}
              />
            </FieldRoot>
            <AddressField
              label={t('onboarding.province')}
              placeholder={t('onboarding.province')}
              value={warehouseAddress.province}
              onChange={(value) => update('province', value)}
            />
            <div className="grid grid-cols-2 gap-4">
              <AddressField
                label={t('onboarding.city')}
                placeholder={t('onboarding.city')}
                value={warehouseAddress.city}
                onChange={(value) => update('city', value)}
                onBlur={() => touch('city')}
                error={errorCity ? t('onboarding.error_city') : ''}
              />
              <AddressField
                label={t('onboarding.postcode')}
                placeholder={t('onboarding.postcode')}
                value={warehouseAddress.postcode}
                onChange={(value) => update('postcode', value)}
                onBlur={() => touch('postcode')}
                invalid={errorPostcode}
              />
            </div>
            <AddressField
              label={t('onboarding.address')}
              placeholder={t('onboarding.address')}
              value={warehouseAddress.addressLine1}
              onChange={(value) => update('addressLine1', value)}
              onBlur={() => touch('addressLine1')}
              invalid={addressLine1Problem === 'required'}
              error={
                addressLine1Problem === 'too_long'
                  ? t('onboarding.error_address')
                  : ''
              }
            />
            <AddressField
              label={t('onboarding.address_details')}
              placeholder={t('onboarding.address_details')}
              value={warehouseAddress.addressLine2}
              onChange={(value) => update('addressLine2', value)}
              onBlur={() => touch('addressLine2')}
              error={
                errorAddressLine2 ? t('onboarding.error_address_details') : ''
              }
            />
          </div>
        </>
      )}
    </ReverCard>
  )
}

interface AddressFieldProps {
  label: string
  placeholder: string
  value: string
  onChange: (value: string) => void
  onBlur?: () => void
  type?: 'text' | 'email' | 'tel'
  error?: string
  // For a field that is marked invalid without a message.
  invalid?: boolean
}

const AddressField: React.FC<AddressFieldProps> = ({
  label,
  placeholder,
  value,
  onChange,
  onBlur,
  type = 'text',
  error = '',
  invalid = false
}) => (
  <FieldRoot invalid={invalid || !!error}>
    <FieldLabel>{label}</FieldLabel>
    <Input
      type={type}
      placeholder={placeholder}
      value={value}
      onChange={(e) => onChange(e.currentTarget.value)}
      onBlur={onBlur}
    />
    {error ? <FieldError match>{error}</FieldError> : null}
  </FieldRoot>
)
